import { CommonModule } from '@angular/common';
import { Component, input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { ToUppercaseDirective } from '@app/shared/pipes/to-uppercase.directive';
import { LegalInformation } from '../company.model';
import { PersonTitle, PersonTitles } from './../../employees/employee.model';

@Component({
  selector: 'app-legal-information-form',
  imports: [
    CommonModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatRadioModule,
    ToUppercaseDirective,
  ],
  templateUrl: './legal-information-form.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./legal-information-form.scss'],
})
export class LegalInformationForm implements OnInit {
  PersonTitles = PersonTitles;
  initWith = input<LegalInformation>();

  form = new FormGroup({
    address: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    postalCode: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(/^\d{5}$/)],
    }),
    city: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    siret: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    siren: new FormControl<string>('', { nonNullable: true }),
    legalRepresentative: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    legalRepresentativeTitle: new FormControl<PersonTitle>('Monsieur', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    legalRepresentativePosition: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    legalForm: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    rcs: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    capital: new FormControl<number>(0, { nonNullable: true, validators: [Validators.required] }),
  });

  /**
   * Ce que tient le registre des entités légales : affiché, jamais modifié dans CDT
   * (RG-societes-lues-dans-les-entites-legales).
   */
  static readonly REGISTRY_FIELDS = [
    'address',
    'postalCode',
    'city',
    'legalForm',
    'siren',
    'siret',
    'rcs',
  ] as const;

  ngOnInit() {
    const initialValue = this.initWith();
    if (initialValue) {
      // Une société arrivée du registre n'a pas encore ce que CDT tient seul : un champ vide se
      // montre vide, et l'API peut ajouter un champ sans que la fiche cesse de s'ouvrir.
      const defaults = this.form.getRawValue();
      this.form.patchValue(
        Object.fromEntries(
          Object.entries(defaults).map(([name, fallback]) => [
            name,
            initialValue[name as keyof LegalInformation] ?? fallback,
          ]),
        ),
      );
    }
    for (const name of LegalInformationForm.REGISTRY_FIELDS) {
      this.form.controls[name].disable();
    }
  }
}
